import { useEffect, useRef, useState } from 'react';
import { FaCheck, FaRegCalendar } from 'react-icons/fa';
import Confetti from 'react-confetti';
import { useRouter } from 'next/router';

import { getAppStoreLink } from 'legacy/common/utils/platform';
import { useViewEvent } from 'features/events/context/ViewEvent';
import Button from 'components/Button';
import Header from '../../../../../components/checkout/Header';

import styles from './styles.module.scss';
import { useCheckout } from '../../context';
import CheckoutTimer from '../CheckoutTimer';
import { useTranslation } from 'react-i18next';

/**
 * Renders the success screen after requesting approval for an event.
 *
 * NOTE: This is the same as the current checkout success screen. This will be changed post release.
 */
const RequestApprovalSuccessStep = () => {
  const { t } = useTranslation(['checkout', 'common', 'events']);
  const router = useRouter();
  const { event } = useViewEvent();
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const confettiZoneRef = useRef<HTMLDivElement>(null);
  const { user } = useCheckout();

  const goToEventPage = () => {
    router.push(`/events/${event._id}`);
  };

  useEffect(() => {
    setHeight(confettiZoneRef.current.clientHeight);
    setWidth(confettiZoneRef.current.clientWidth);
  }, []);

  return (
    <>
      <Header
        onBack={goToEventPage}
        title={t('checkout:requestSent')}
        subtitle={event.title}
        isUserLoggedIn={!!user}
        TimerComponent={CheckoutTimer}
      />
      <div className="md:mt-[62px] -mt-[34px] h-full relative flex flex-col items-center justify-start overflow-x-hidden">
        <div
          className="w-full bg-primary-200 flex flex-col items-center p-10"
          ref={confettiZoneRef}
        >
          <Confetti
            width={width}
            height={height}
            numberOfPieces={100}
            colors={['#643DC4', '#F98700', '#7994f7']}
            drawShape={(ctx) => {
              ctx.beginPath();
              ctx.arc(0.2, 0.2, 4, 0, 2 * Math.PI);
              ctx.fill();

              ctx.closePath();
            }}
          />
          <div className={styles.scaleIn}>
            <div className="flex items-center justify-center">
              <div className={styles.eventPhoto}>
                <img
                  className="w-24 h-24 object-cover rounded-full shadow-xl"
                  src={event.photo.url}
                  alt={event.title}
                />
              </div>
              <div className={styles.checkMark}>
                <div className="w-24 h-24 object-cover rounded-full bg-primary-400 flex items-center justify-center shadow-xl">
                  <FaCheck size={40} color="white" />
                </div>
              </div>
            </div>
          </div>
          <h1 className="mt-10 text-2xl font-bold">
            {t('checkout:requestSent')}
          </h1>
          <p className="mt-4 text-lg text-gray-600 font-normal text-center">
            {t('checkout:stayTunedForApprovalToPurchaseTickets')}
          </p>
        </div>
        <div className="md:flex md:flex-row-reverse md:px-16 md:mt-4 px-8 w-full justify-between">
          {/* Mobile View */}
          <a
            href={getAppStoreLink()}
            target="_blank"
            rel="noreferrer"
            className="block md:hidden mt-4 mb-10 md:mt-0 md:w-1/2 md:px-4 cursor-pointer mx-auto"
          >
            <div className="flex shadow-lg rounded-lg overflow-hidden border-2 border-primary-400">
              <img
                src="/img/events/download-app-vertical.png"
                className="object-cover h-full w-28"
              />
              <div className="pt-4 px-4">
                <p className="mt-4 text-base font-semibold">
                  {t('common:getAppFooter.downloadTheApp')}
                </p>
                <p className="mt-2 text-sm">
                  {t('common:getAppFooter.openInTheAppToGetTheFullExperience')}
                </p>
              </div>
            </div>
          </a>

          {/* Desktop View */}
          <a
            href={getAppStoreLink()}
            target="_blank"
            rel="noreferrer"
            className="hidden md:block mt-4 mb-10 md:mt-0 md:px-4 cursor-pointer mx-auto"
          >
            <div className="shadow-lg rounded-lg overflow-hidden border-2 border-primary-400">
              <img
                src="/img/events/download-app.png"
                className="w-full object-cover"
              />
              <div className="p-4">
                <p className="mt-4 text-base font-semibold">
                  {t('common:getAppFooter.downloadTheApp')}
                </p>
                <p className="mt-2 text-sm">
                  {t('common:getAppFooter.openInTheAppToGetTheFullExperience')}
                </p>
              </div>
            </div>
          </a>
        </div>
      </div>
      <div className="mt-10 absolute bottom-0 w-full">
        <div className={styles.boxShadow}>
          <div className="px-4 w-full py-8 bg-white max-w-[650px] mx-auto">
            <Button
              variant="primaryDark"
              size="large"
              className="w-full"
              onClick={goToEventPage}
            >
              <div className="flex items-center justify-center">
                <FaRegCalendar className="mr-2" />
                {t('events:actions.viewEvent')}
              </div>
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default RequestApprovalSuccessStep;
